import { __assign, __read, __rest } from "tslib";
import React, { memo, useContext, useEffect, useRef, useState, } from 'react';
import { Field } from 'react-final-form';
import { NutsSelector } from '@mercell/nuts-selector-react';
import FieldError from '@mercell/field-error-react';
import stringify from 'fast-safe-stringify';
import { FormContext } from '../utils/FormContext';
import { prepareAndToggleFieldErrors } from '../utils/prepareAndToggleFieldErrors';
import { TrackFieldValueByName } from '../utils/TrackFieldValueByName';
import { resolveUseSamePath } from '../utils/resolveUseSamePath';
import { nestedResolveUseSamePath } from '../utils/nestedResolveUseSamePath';
import { nl2br } from '../utils/nl2br';
var UpdateIfTrackedValueChanged = function (_a) {
    var onChange = _a.onChange, nutsValue = _a.nutsValue, trackedFieldValue = _a.trackedFieldValue;
    var trackedFieldValueRef = useRef(trackedFieldValue);
    useEffect(function () {
        if (trackedFieldValue !== trackedFieldValueRef.current && nutsValue) {
            onChange('');
        }
        if (trackedFieldValue !== trackedFieldValueRef.current) {
            trackedFieldValueRef.current = trackedFieldValue;
        }
    }, [nutsValue, onChange, trackedFieldValue]);
    return null;
};
export var NutsSelectorField = memo(function (_a) {
    var _b, _c, _d;
    var field = _a.field, name = _a.name;
    var _e = field, fieldToTrack = _e.fieldToTrack, _f = _e.fieldLabel, fieldLabel = _f === void 0 ? function () { return null; } : _f, validations = _e.validations, _g = _e.fieldWrapperProperties, fieldWrapperProperties = _g === void 0 ? {} : _g, priorityCountryAbbr = _e.priorityCountryAbbr, disableValidationIfNoNuts = _e.disableValidationIfNoNuts, associatedValidationBasedOnOtherFieldValues = _e.associatedValidationBasedOnOtherFieldValues, arrayOfFieldsToRevalidateOnChange = _e.arrayOfFieldsToRevalidateOnChange, _h = _e.partialFieldProps, partialFieldProps = _h === void 0 ? {} : _h, restProps = __rest(_e, ["fieldToTrack", "fieldLabel", "validations", "fieldWrapperProperties", "priorityCountryAbbr", "disableValidationIfNoNuts", "associatedValidationBasedOnOtherFieldValues", "arrayOfFieldsToRevalidateOnChange", "partialFieldProps"]);
    var _j = __read(useState(false), 2), isValidationDisabled = _j[0], toggleValidationStatus = _j[1];
    var trackedFieldValue;
    if (fieldToTrack && fieldToTrack.fieldName) {
        var fieldName = fieldToTrack.fieldName, useSamePath = fieldToTrack.useSamePath;
        trackedFieldValue = TrackFieldValueByName(name, fieldName, useSamePath);
    }
    var _k = __read(useState(false), 2), reApplyRequiredIndicator = _k[0], setReApplyRequiredIndicator = _k[1];
    var _l = useContext(FormContext), validationCallback = _l.validationCallback, showErrorsOnInitialRender = _l.showErrorsOnInitialRender;
    var validateMethod = function () { return undefined; };
    var updatedConditions = nestedResolveUseSamePath((_c = (_b = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _b === void 0 ? void 0 : _b.conditions) !== null && _c !== void 0 ? _c : [], name);
    var updatedAssociatedValidationBasedOnOtherFieldValues = {
        logicalOperator: (_d = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _d === void 0 ? void 0 : _d.logicalOperator,
        conditions: updatedConditions,
    };
    if ((validations || associatedValidationBasedOnOtherFieldValues) &&
        validationCallback) {
        validateMethod = function (shouldStopValidation) {
            return shouldStopValidation
                ? function () { return null; }
                : function (fieldValue, allValues) {
                    return validationCallback(fieldValue, validations, allValues, {
                        conditionalFields: updatedAssociatedValidationBasedOnOtherFieldValues,
                        rules: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.rules,
                        message: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.message,
                        reApplyRequiredIndicator: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.reApplyRequiredIndicator,
                    }, setReApplyRequiredIndicator);
                };
        };
    }
    var nutsFetchCallback;
    if (disableValidationIfNoNuts) {
        nutsFetchCallback = function (nuts) {
            if (nuts && nuts.length === 0 && !isValidationDisabled) {
                toggleValidationStatus(true);
            }
            else if (nuts && nuts.length && isValidationDisabled) {
                toggleValidationStatus(false);
            }
        };
    }
    var validateFields = arrayOfFieldsToRevalidateOnChange === null || arrayOfFieldsToRevalidateOnChange === void 0 ? void 0 : arrayOfFieldsToRevalidateOnChange.map(function (fieldToRevalidate) {
        if (fieldToRevalidate.useSamePath) {
            return resolveUseSamePath(name, fieldToRevalidate.fieldName);
        }
        return fieldToRevalidate.fieldName;
    });
    var _m = partialFieldProps.parse, parse = _m === void 0 ? function (val) { return val; } : _m;
    return (React.createElement(Field, __assign({}, partialFieldProps, { parse: parse, name: name, validate: validateMethod && validateMethod(isValidationDisabled), 
        // https://final-form.org/docs/react-final-form/types/FieldProps#validate
        // https://codesandbox.io/s/changing-field-level-validators-zc8ei?fontsize=14
        key: isValidationDisabled ? 0 : 1, validateFields: validateFields }), function (_a) {
        var input = _a.input, meta = _a.meta;
        var _b = prepareAndToggleFieldErrors(meta, showErrorsOnInitialRender), showErrors = _b.showErrors, errors = _b.errors;
        return (React.createElement("div", __assign({}, fieldWrapperProperties),
            React.createElement(UpdateIfTrackedValueChanged, { onChange: input.onChange, trackedFieldValue: trackedFieldValue, nutsValue: input.value }),
            fieldLabel(name, reApplyRequiredIndicator),
            React.createElement(NutsSelector, __assign({}, input, restProps, { priorityCountryAbbr: trackedFieldValue || priorityCountryAbbr, nutsFetchCallback: nutsFetchCallback, onChangeCallback: function (selectedCodes) {
                    return input.onChange(selectedCodes);
                }, isDisabled: restProps.disabled })),
            showErrors && !isValidationDisabled && (React.createElement(FieldError, null, nl2br(errors)))));
    }));
}, function (prevProps, nextProps) {
    try {
        return stringify(prevProps) === stringify(nextProps);
    }
    catch (e) {
        console.warn("NutsSelectorField: ".concat(e));
        return false;
    }
});
NutsSelectorField.displayName = 'NutsSelectorField';
